import { Component, OnInit, DestroyRef, inject, computed, signal } from '@angular/core';
import { RouterOutlet, Router, RouterLink, NavigationEnd } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { AuthService } from '@core/auth/auth.service';
import { readAppVersion } from '@core/version/app-version';
import { environment } from '@env/environment';
import { filter, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeedbackAccessService } from '@core/services/feedback-access.service';
import { ApiConnectionState } from '@core/http/api-connection.interceptor';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, RouterLink, HeaderComponent, MatToolbarModule, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss'
})
export class MainLayoutComponent implements OnInit {
  readonly feedbackAccess = inject(FeedbackAccessService);
  readonly apiConnection = inject(ApiConnectionState);
  readonly userError = toSignal(inject(UserFullInfoService).getError$(), { initialValue: null });
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly appVersion = signal<string | null>(null);
  readonly versionFooterText = computed(() => {
    const version = this.appVersion();

    if (!version) {
      return null;
    }

    const appEnvironment = environment.appEnvironment;
    const environmentSuffix = appEnvironment && appEnvironment !== 'production' ? ` - ${appEnvironment}` : '';

    return `INA Aplicaciones v${version}${environmentSuffix}`;
  });

  constructor(
    private userFullInfoService: UserFullInfoService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    void this.loadAppVersion();
    this.authService.isAuthenticated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(authenticated => {
      if (authenticated) void this.feedbackAccess.refresh(); else this.feedbackAccess.reset();
    });
    this.router.events.pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.authService.isLoggedIn()) void this.feedbackAccess.refresh();
    });

    if (this.authService.isLoggedIn()) {
      this.loadUserInfo();
      return;
    }

    this.authService.isAuthenticated$
      .pipe(
        filter(Boolean),
        take(1)
      )
      .subscribe(() => this.loadUserInfo());
  }

  newTask(): void { void this.router.navigate(['/feedback/new']); }

  async retryConnection(): Promise<void> {
    this.apiConnection.retry();
    try {
      await Promise.all([this.userFullInfoService.refreshUserInfo(), this.feedbackAccess.refresh()]);
    } catch {}
  }

  private loadUserInfo(): void {
    this.userFullInfoService.loadUserInfo().catch((error: unknown) => {
      console.error('Error cargando información del usuario:', error);
    });
  }

  private async loadAppVersion(): Promise<void> {
    const version = await readAppVersion();

    if (version) {
      this.appVersion.set(version);
    }
  }
}
